import LinkifyIt from 'linkify-it';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import {LinkifyRenderer} from '../../../../srcCommon/formatters/markdown/linkifyRenderer';
import {ArbitraryCalendarEvent} from '../../../models/api/arbitraryCalendarEventModel';
import {CalendarEventVersionLocation} from '../../../models/api/calendarEventVersionModel';

/*
 * Constants.
 */

const linkify = LinkifyIt();
const googleMapsBaseUrl = 'https://www.google.com/maps/search/?api=1&query=';

/*
 * Props.
 */

interface CalendarEventLocationRendererProps {
  location: CalendarEventVersionLocation | ArbitraryCalendarEvent['locations'][number];
}

/*
 * Style.
 */

const StyledMapLinkButton = styled.span`
  color: ${(p) => p.theme.palette.blue.shade40};
  cursor: pointer;
`;

const StyledMapWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`;

/*
 * Component.
 */

export const CalendarEventLocationRenderer: FC<CalendarEventLocationRendererProps> = (props) => {
  const {location} = props;
  const locationText = location.address || location.displayName || '';
  const locationTextWithoutURLs = useMemo(() => {
    const matches = linkify.match(locationText);

    // If no URLs in the location text, return the original text.
    if (!matches) {
      return locationText;
    }

    // Otherwise, remove the URLs found from the location text so we don't include them when searching on Google Maps.
    const regex = new RegExp(`${matches.map((i) => i.raw).join('|')}`, 'g');

    return locationText.replace(regex, '').trim();
  }, [locationText]);
  const needsMapButton = locationTextWithoutURLs.length > 0;

  return (
    <>
      <LinkifyRenderer text={locationText} />
      {needsMapButton && (
        <StyledMapWrapper>
          <span>&mdash;</span>
          <StyledMapLinkButton onClick={() => openMapLink(locationTextWithoutURLs)}>
            <Trans id="invite-details-map">Map</Trans>
          </StyledMapLinkButton>
        </StyledMapWrapper>
      )}
    </>
  );
};

function openMapLink(address: string) {
  openInBrowser(googleMapsBaseUrl + address);
}
